import { TAppDispatch, TRootState } from 'store/types'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { voiceCatalogThunks } from 'store/features/voiceCatalog/thunks'
import { voiceCatalogSelectors } from 'store/features/voiceCatalog/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { getSpeakerIdByGender, getSpeakersCacheKey } from 'utils/tts'
import { AVATAR_TTS_VOICE_MODEL } from 'consts/tts'

const { actions } = slice

// Sets the avatar voice language and auto-selects a default speaker for it. When
// a public avatar is selected, keeps the voice matched to its gender (falls back
// to the first usable speaker if no gender match); otherwise picks the first
// usable speaker (mirrors the TTS bulk behavior).
const setVoiceLanguage =
  (langCode: string) =>
  async (dispatch: TAppDispatch, getState: () => TRootState) => {
    dispatch(actions.setVoiceLangCode(langCode))

    const gender = aiAvatarSelectors.selectedPublicAvatarGender(getState())
    if (gender) {
      await dispatch(voiceThunks.selectSpeakerByGender(gender))
      return
    }

    dispatch(
      voiceCatalogThunks.getSpeakersPerLangCode(
        langCode,
        firstSpeakerId => {
          dispatch(actions.setVoiceSpeakerId(firstSpeakerId))
        },
        AVATAR_TTS_VOICE_MODEL
      )
    )
  }

// Auto-selects a voice matching the given gender (e.g. from the picked public
// avatar) among the current language's speakers. Ensures the speaker list is
// loaded first; leaves the current speaker untouched if no match is found.
const selectSpeakerByGender =
  (gender: string) =>
  async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const langCode = aiAvatarSelectors.voiceLangCode(getState())

    await dispatch(
      voiceCatalogThunks.getSpeakersPerLangCode(
        langCode,
        undefined,
        AVATAR_TTS_VOICE_MODEL
      )
    )

    const state = getState()
    const cacheKey = getSpeakersCacheKey(langCode, AVATAR_TTS_VOICE_MODEL)
    const speakers = voiceCatalogSelectors.speakersPerLangCodeSelector(
      state,
      cacheKey
    )
    const isFreeUser = userSelectors.isFreeUser(state)

    const speakerId = getSpeakerIdByGender(speakers, gender, isFreeUser)
    if (speakerId !== null) {
      dispatch(actions.setVoiceSpeakerId(speakerId))
    }
  }

// Applies a fully-specified voice selection (language + exact speaker), used by
// the landing handoff. Unlike setVoiceLanguage it does NOT auto-pick a speaker:
// it awaits the speaker catalog load (so the picker has the list) and then sets
// the caller's speaker last, so no late catalog callback can clobber it.
const setVoiceSelection =
  (langCode: string, speakerId: number) => async (dispatch: TAppDispatch) => {
    dispatch(actions.setVoiceLangCode(langCode))
    await dispatch(
      voiceCatalogThunks.getSpeakersPerLangCode(
        langCode,
        undefined,
        AVATAR_TTS_VOICE_MODEL
      )
    )
    dispatch(actions.setVoiceSpeakerId(speakerId))
  }

export const voiceThunks = {
  setVoiceLanguage,
  selectSpeakerByGender,
  setVoiceSelection
}
